import { Loader } from '@openx/components-core/lib/Loader/Loader';
import {
  GeographicOption,
  type GeographicTargetingApi,
  type GeographicTargetingState,
} from '@openx/types/targeting/geographic';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomOptionsProvider, useCustomOptionsContext } from '../../utils/hooks/useCustomOptionsContext';
import { useRemoveParameterUpdater } from '../../utils/hooks/useRemoveParameterUpdater';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { geographicDefaultOptions, getDefaultGeographicState } from './constants';
import { GeographicItems } from './GeographicItems';
import { GeographicReadOnly } from './GeographicReadOnly';
import { GeographicTopBox } from './GeographicTopBox/GeographicTopBox';
import { useGetGeoState } from './hooks/useGetGeoState';
import { isGeoEmpty, mapGeographicStateToApi } from './utils';

type GeographicApi = NonNullable<ReturnType<typeof mapGeographicStateToApi>>;

export const Geographic = memo(function Geographic({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  customOptions = geographicDefaultOptions,
}: TargetingProps<GeographicTargetingApi>) {
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);
  const { t } = useTranslation();
  const { geographicBaseState, geographicState, isDataUpdated, setGeographicBaseState, setGeographicState } =
    useGetGeoState(targetingParams);

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => {
      onFieldUpdateExtended(field, mapGeographicStateToApi(geographicState));
      setGeographicBaseState(geographicState);
    },
    restoreParams: () => geographicBaseState && setGeographicState(geographicBaseState),
  });

  const topBoxCustomOptions = useCustomOptionsContext<GeographicOption>();
  const [option, setOption] = useState<GeographicOption>(
    customOptions?.defaultOption ?? topBoxCustomOptions?.defaultOption ?? GeographicOption.INCLUDES
  );

  const onRemoveAll = useCallback(() => {
    onFieldUpdateExtended(field, null);
    setGeographicBaseState(getDefaultGeographicState());
    setGeographicState(getDefaultGeographicState());
  }, [onFieldUpdateExtended, field, setGeographicBaseState, setGeographicState]);

  const onRemoveParameter = useRemoveParameterUpdater<
    GeographicTargetingState,
    GeographicOption,
    GeographicApi,
    typeof field
  >({
    field,
    onFieldUpdate,
    setState: setGeographicState,
    getDefaults: () => getDefaultGeographicState(),
    mapStateToApi: mapGeographicStateToApi,
    afterSetState: (next, option, defaults) => {
      if (option === GeographicOption.INCLUDES) {
        next[GeographicOption.INCLUDES] = defaults[GeographicOption.INCLUDES];
        next[GeographicOption.EXCLUDES] = defaults[GeographicOption.EXCLUDES];
        next[GeographicOption.EXCLUDES_SUBSET] = defaults[GeographicOption.EXCLUDES_SUBSET];
      }

      setGeographicBaseState(prev =>
        prev
          ? {
              ...prev,
              ...(option === GeographicOption.INCLUDES
                ? {
                    [GeographicOption.INCLUDES]: defaults[GeographicOption.INCLUDES],
                    [GeographicOption.EXCLUDES]: defaults[GeographicOption.EXCLUDES],
                    [GeographicOption.EXCLUDES_SUBSET]: defaults[GeographicOption.EXCLUDES_SUBSET],
                  }
                : {
                    [option]: defaults[option],
                  }),
            }
          : prev
      );
    },
  });

  const isEmpty = useMemo(() => isGeoEmpty(geographicState), [geographicState]);

  const isValidFormat = (type: string): type is GeographicOption => {
    return Object.values(GeographicOption).includes(type as GeographicOption);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<GeographicOption>(
    isValidFormat,
    onDrawerOpen,
    setOption
  );

  if (!geographicBaseState) {
    return <Loader />;
  }

  return (
    <CustomOptionsProvider value={customOptions}>
      <GeographicReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        readonly={readonly}
        targetingParams={geographicBaseState}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={onRemoveAll}
        onRemoveParameter={onRemoveParameter}
      />
      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <GeographicTopBox
            targetingParams={geographicState}
            onChange={setGeographicState}
            option={option}
            setOption={setOption}
          />

          {isEmpty ? (
            <EmptyFilterMessage text={t('No filters applied.')} />
          ) : (
            <GeographicItems targetingParams={geographicState} readonly={false} onChange={setGeographicState} />
          )}
        </TargetingDrawer>
      )}
    </CustomOptionsProvider>
  );
});
